import React, { CSSProperties } from "react";

import LoadingButton, { LoadingButtonProps } from "@mui/lab/LoadingButton";
import { IconButton, Tooltip } from "@mui/material";

import ConfirmModal from "src/common/components/ui/ConfirmModal";

interface ConfirmButtonProps extends LoadingButtonProps {
  showModalFunc: () => boolean;
  onSubmit: () => Promise<void>;
  confirmText: string | React.ReactElement;
  confirmButtonText: string;
  modalSx?: CSSProperties;
}

interface ConfirmIconButtonProps extends LoadingButtonProps {
  showModalFunc: () => boolean;
  onSubmit: () => Promise<void>;
  confirmText: string;
  confirmButtonText: string;
  modalSx?: CSSProperties;
  tooltip?: string;
  icon: any;
}

const ConfirmButton: React.FunctionComponent<ConfirmButtonProps> = ({
  onSubmit,
  showModalFunc,
  confirmText,
  confirmButtonText,
  modalSx,
  ...props
}) => {
  const [confirmed, setConfirmed] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);

  React.useEffect(() => {
    const submit = async () => {
      await onSubmit();
    };

    if (confirmed) {
      if (showModal) {
        setShowModal(false);
      }

      submit();
    }

    return function cleanup() {
      setConfirmed(false);
    };
  }, [confirmed, showModal, onSubmit]);

  const btnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (showModalFunc()) {
      setShowModal(true);
    } else {
      setConfirmed(true);
    }
  };

  const onCancel = () => {
    setShowModal(false);
  };

  return (
    <>
      {showModal && (
        <ConfirmModal
          sx={modalSx}
          confirmButtonText={confirmButtonText}
          confirmText={confirmText}
          onCancel={onCancel}
          setStatus={setConfirmed}
        />
      )}
      <LoadingButton {...props} onClick={btnClick}>
        {props.children}
      </LoadingButton>
    </>
  );
};

export const ConfirmIconButton: React.FunctionComponent<ConfirmIconButtonProps> = ({
  onSubmit,
  showModalFunc,
  confirmText,
  confirmButtonText,
  modalSx,
  tooltip,
  icon,
  ...props
}) => {
  const [confirmed, setConfirmed] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);

  React.useEffect(() => {
    const submit = async () => {
      await onSubmit();
    };

    if (confirmed) {
      if (showModal) {
        setShowModal(false);
      }

      submit();
    }

    return function cleanup() {
      setConfirmed(false);
    };
  }, [confirmed, showModal, onSubmit]);

  const btnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (showModalFunc()) {
      setShowModal(true);
    } else {
      setConfirmed(true);
    }
  };

  const onCancel = () => {
    setShowModal(false);
  };

  return (
    <>
      {showModal && (
        <ConfirmModal
          sx={modalSx}
          confirmButtonText={confirmButtonText}
          confirmText={confirmText}
          onCancel={onCancel}
          setStatus={setConfirmed}
        />
      )}
      <Tooltip title={tooltip}>
        {/* We wrap IconButton in a span to allow the tooltip to still function even when the button is disabled. */}
        <span role={"tooltip"}>
          <IconButton {...props} onClick={btnClick}>
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};

export default ConfirmButton;
